import axios from 'src/utils/axios';

type DVOpts = { actingOnMemberId?: number | null };

export interface SurveySection {
  id: string;
  name: string;
  status: 'not_started' | 'incomplete' | 'complete';
  answeredCount: number;
  totalCount: number;
  lastAnsweredAt: string | null;
}

export interface SurveyQITAnswer {
  qitId: number;
  label: string;
  questionType: string;
  displayValue: string;
  lastUpdatedAt: string | null;
  isStale: boolean;
  files: Array<{ id: number; name: string }>;
  answeredAt: string | null;
  groupId: number | null;
  groupName: string | null;
  isMulti: boolean;
  answerGroupId: number | null;
  instanceLabel: string | null;
}

export interface SurveyMeta {
  id: number;
  is_profile: boolean;
  cert_id: number | null;
  cert_name: string | null;
  property_name: string | null;
  cert_category: string | null;
  effective_date: string | null;
  member_name: string | null;
  renter_active: boolean;
}

export function basePath(surveyId: number, opts: DVOpts): string {
  return opts.actingOnMemberId
    ? `data-vault/members/${opts.actingOnMemberId}/surveys/${surveyId}`
    : `surveys/${surveyId}`;
}

export const getSurveySections = (
  surveyId: number,
  opts: DVOpts = {}
): Promise<SurveySection[]> =>
  axios
    .get<{ sections: SurveySection[] }>(`${basePath(surveyId, opts)}/sections/`)
    .then((r) => r.data.sections);

export const getSurveySectionAnswers = (
  surveyId: number,
  sectionId: string | number,
  opts: DVOpts = {}
): Promise<SurveyQITAnswer[]> =>
  axios
    .get<{ answers: SurveyQITAnswer[] }>(
      `${basePath(surveyId, opts)}/sections/${sectionId}/answers/`
    )
    .then((r) => r.data.answers);

export const getSurveyMeta = (
  surveyId: number,
  opts: DVOpts = {}
): Promise<SurveyMeta> =>
  axios
    .get<SurveyMeta>(`${basePath(surveyId, opts)}/meta/`)
    .then((r) => r.data);

// ─── SurveySummary (full sections + groups + answers) ──────────────────────────

export interface SurveySummaryAnswer {
  qitId: number;
  label: string;
  questionType: string;
  displayValue: string;
  lastUpdatedAt: string | null;
  numberOfFilesRequired: number;
  files: Array<{ id: number; name: string }>;
}

export interface SurveySummaryGroup {
  groupId: number;
  groupName: string;
  isMulti: boolean;
  answerGroupId: number | null;
  instanceLabel: string | null;
  answers: SurveySummaryAnswer[];
}

export interface SurveySummarySection {
  id: string;
  name: string;
  status: 'not_started' | 'incomplete' | 'complete';
  answeredCount: number;
  totalCount: number;
  groups: SurveySummaryGroup[];
}

export const getSurveySummary = (
  surveyId: number
): Promise<SurveySummarySection[]> =>
  axios
    .get<{ sections: SurveySummarySection[] }>(`surveys/${surveyId}/summary/`)
    .then((r) => r.data.sections);
